import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { runManage, runSync } from '../../services/esetApi';
import type { EsetBatchResult } from '../../services/esetApi';

export type BatchKind = 'manage' | 'sync';

interface EsetBatchRunDialogProps {
  kind: BatchKind | null; // null = closed
  onClose: () => void;
  /** Called after a REAL run completes, so the table can refresh. */
  onRealRun?: () => void;
}

const COPY: Record<BatchKind, { title: string; desc: string }> = {
  manage: {
    title: 'Run manage (renewal/billing lifecycle)',
    desc: 'Sends renewal warnings, raises renewal invoices, suspends/lapses, and logs timeline events. A dry-run writes nothing (and emits no events); a real run performs actions — billing stays gated by ENABLE_BILLING and emails by ENABLE_NOTIFICATIONS.',
  },
  sync: {
    title: 'Run sync (ESET → Dataverse)',
    desc: 'Reconciles ESET companies + licences into the managed tables and refreshes cadence + service status. A dry-run writes nothing.',
  },
};

export const EsetBatchRunDialog: React.FC<EsetBatchRunDialogProps> = ({ kind, onClose, onRealRun }) => {
  const [dryRun, setDryRun] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<EsetBatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!kind) return null;
  const copy = COPY[kind];

  const reset = () => {
    setDryRun(true);
    setConfirming(false);
    setResult(null);
    setError(null);
    setRunning(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const execute = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await (kind === 'manage' ? runManage(dryRun) : runSync(dryRun));
      setResult(res);
      if (!dryRun) onRealRun?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  };

  const onRunClick = () => {
    if (!dryRun && !confirming) {
      setConfirming(true); // real run needs a second, explicit confirm click
      return;
    }
    execute();
  };

  const runLabel = dryRun
    ? 'Run dry-run'
    : confirming
      ? 'Confirm — run for real now'
      : 'Run for real';

  return (
    <Dialog open onClose={running ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: 13, color: '#555', mb: 1.5 }}>{copy.desc}</Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={dryRun}
              disabled={running}
              onChange={(e) => {
                setDryRun(e.target.checked);
                setConfirming(false);
              }}
            />
          }
          label="Dry run (no writes)"
        />

        {!dryRun && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Real run — this performs actual {kind} actions{kind === 'manage' ? ' (suspend / lapse / invoice / emails, per the flags)' : ''}.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

        {result && (
          <Box sx={{ mt: 1.5 }}>
            <Alert severity={result.dry_run ? 'info' : 'success'} sx={{ mb: 1 }}>
              {result.dry_run ? 'Dry-run complete — nothing written.' : 'Run complete.'}
            </Alert>
            <Box
              component="pre"
              sx={{
                m: 0, p: 1.5, fontSize: 11, lineHeight: 1.5,
                background: '#f0f4f9', borderRadius: '6px',
                border: '0.5px solid #e2eaf3', maxHeight: 300, overflow: 'auto',
              }}
            >
              {JSON.stringify(result, null, 2)}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={running} sx={{ color: '#aaa' }}>
          Close
        </Button>
        <Button
          variant="contained"
          color={dryRun ? 'primary' : 'error'}
          onClick={onRunClick}
          disabled={running}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {running ? 'Running…' : runLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
